import { Injectable } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HTTP_INTERCEPTORS,
  HttpClient,
  HttpErrorResponse
} from '@angular/common/http';
import { Router } from '@angular/router';
import {
  Observable,
  EMPTY,
  throwError,
  BehaviorSubject
} from 'rxjs';
import {
  catchError,
  tap,
  filter,
  first,
  concatMap
} from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { State } from '@app/models';
import { HttpStatusCodes } from '@app/constants';
import { getStoredAuth, updateStoredTokens } from '../auth-base.utils';
import { AuthBaseApi } from '../auth-base.api';
import { InvalidateAuthorization, RefreshTokenLocalSuccess, SetReferer } from '../auth-base.action';

const UNAUTHORIZED_TOKEN_CODES = ['unauthorized', `${HttpStatusCodes.UNAUTHORIZED}`];
const FORBIDDEN_TOKEN_CODES = ['invalidRefreshToken', `${HttpStatusCodes.FORBIDDEN}`];

@Injectable()
export class RefreshTokenInterceptor implements HttpInterceptor {

  private _refreshToken$ = new BehaviorSubject<string>(null);
  private _refreshing = false;

  constructor(
    private _store: Store<State>,
    private _authApi: AuthBaseApi,
    private _http: HttpClient,
    private _router: Router
  ) { }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>>  {
    const data = getStoredAuth();

    return next.handle(request).pipe(
      catchError((err: HttpErrorResponse) => {
        if (UNAUTHORIZED_TOKEN_CODES.includes(err.error.error.code) && data && data.auth.refreshToken) {

          if (!this._refreshing) {
            this._refreshing = true;
            this._refreshToken$.next(null);

            return this._authApi
              .refresh$(data.auth.refreshToken)
              .pipe(
                tap((payload) => {
                  updateStoredTokens(payload);
                  this._store.dispatch(new RefreshTokenLocalSuccess(
                    payload.accessToken,
                    payload.refreshToken,
                    payload.expiresIn,
                    payload.expiresAt
                  ));
                  this._refreshToken$.next(payload.accessToken);
                }),
                concatMap(() => this._http.request(request)),
                catchError((innerErr: HttpErrorResponse) => {
                  if (
                    UNAUTHORIZED_TOKEN_CODES.includes(innerErr.error.error.code) ||
                    FORBIDDEN_TOKEN_CODES.includes(innerErr.error.error.code)
                  ) {
                    // invalidate authorization
                    this._store.dispatch(new InvalidateAuthorization(true));
                    // set referer
                    this._store.dispatch(new SetReferer(this._router.url));
                  }

                  this._refreshing = false;

                  return EMPTY;
                })
              );

          } else {
            return this._refreshToken$.pipe(
              filter((accessToken) => !!accessToken),
              first(),
              tap((accessToken) => {
                const storedAuth = getStoredAuth();
                const auth = storedAuth ? storedAuth.auth : undefined;
                this._store.dispatch(new RefreshTokenLocalSuccess(
                  accessToken,
                  auth ? auth.refreshToken : undefined,
                  auth ? auth.expiresIn : undefined,
                  auth ? auth.expiresAt : undefined
                ));
                this._refreshing = false;
              }),
              concatMap(() => this._http.request(request))
            );
          }

        } else {
          return throwError(() => err);
        }
      })
    );
  }
}

export const refreshTokenInterceptorProvider = {
  provide: HTTP_INTERCEPTORS,
  useClass: RefreshTokenInterceptor,
  multi: true
};
